import type { WorkspaceViewId } from "./navItems";

export type PendingSearchSelection = {
  frameRecordId: string;
  nodeId: string;
};

/**
 * Selecting an entity must not yank the user out of the Private Agent chat view;
 * every other view routes the selection to the graph. Single authority for the
 * mouse path, the keyboard path, and the cross-frame restore path.
 */
export function viewAfterNodeSelect(activeView: WorkspaceViewId): WorkspaceViewId {
  return activeView === "privateAgent" ? "privateAgent" : "graph";
}

type FrameRestoreSelectionOutcome =
  | { kind: "idle" }
  | { kind: "stale" }
  | { kind: "selected"; nodeId: string }
  | { kind: "unavailable"; nodeId: string };

/**
 * A pending cross-frame selection is consumed exactly once by the frame that it
 * targets: either the node is there ("selected") or it is gone ("unavailable").
 * Both are terminal — leaving the pending selection set after a restore strands
 * the side panel on a spinner nothing can clear.
 */
function resolveFrameRestoreSelection(input: {
  pending: PendingSearchSelection | null;
  restoredFrameRecordId: string;
  restoredNodeIds: readonly string[];
}): FrameRestoreSelectionOutcome {
  const { pending, restoredFrameRecordId, restoredNodeIds } = input;
  if (!pending) {
    return { kind: "idle" };
  }

  if (pending.frameRecordId !== restoredFrameRecordId) {
    // The user navigated to some other frame before this one loaded; the pending
    // jump can never resolve, so drop it instead of reporting a failed lookup.
    return { kind: "stale" };
  }

  return restoredNodeIds.includes(pending.nodeId)
    ? { kind: "selected", nodeId: pending.nodeId }
    : { kind: "unavailable", nodeId: pending.nodeId };
}

export type SearchSelectionNotice = {
  tone: "alert" | "status";
  message: "loading" | "unavailable";
  retry: boolean;
};

/**
 * What the Private Agent side panel says about a cross-frame jump. `loading` is
 * only ever reachable while a pending jump is still in flight, so no terminal
 * state can leave a spinner on screen.
 */
export function searchSelectionNotice(input: {
  pending: PendingSearchSelection | null;
  restoreFailed: boolean;
  unavailableNodeId: string | null;
}): SearchSelectionNotice | null {
  const { pending, restoreFailed, unavailableNodeId } = input;
  if (!pending && !unavailableNodeId) {
    return null;
  }

  if (restoreFailed) {
    return { tone: "alert", message: "unavailable", retry: true };
  }

  if (unavailableNodeId) {
    return { tone: "alert", message: "unavailable", retry: false };
  }

  return { tone: "status", message: "loading", retry: false };
}

export type SearchSelectionState = {
  /** In-flight cross-frame jump, consumed by the frame it targets. */
  pending: PendingSearchSelection | null;
  /** Terminal failure of a jump: the restored frame no longer carries the node. */
  unavailable: string | null;
};

export const initialSearchSelectionState: SearchSelectionState = { pending: null, unavailable: null };

/** Anything the user starts by hand; none of these can be a jump outcome. */
export type SearchSelectionUserEvent =
  | { kind: "cross-frame-jump"; frameRecordId: string; nodeId: string }
  | { kind: "select-node" }
  | { kind: "select-edge" }
  | { kind: "change-view" };

/** A committed frame finished loading — the only place a jump is adjudicated. */
export type SearchSelectionRestoreEvent = {
  kind: "frame-restored";
  frameRecordId: string;
  nodeIds: readonly string[];
};

export type SearchSelectionEvent = SearchSelectionUserEvent | SearchSelectionRestoreEvent;

export type SearchSelectionStep = {
  state: SearchSelectionState;
  /** Node the caller must select as a side effect; null when there is nothing to select. */
  selectNodeId: string | null;
};

/**
 * The single authority for cross-frame jump state. Callers own only the side
 * effects (selecting a node, navigating); they must never decide `pending` or
 * `unavailable` themselves, or the "unavailable notice outlives its frame" bug
 * comes back one handler at a time.
 *
 * Two invariants hold over the whole event set:
 * - any user-initiated event leaves `unavailable` null — a new action retires
 *   the failure notice from the previous one, wherever it was raised;
 * - any frame restore leaves `pending` null — the jump is consumed exactly
 *   once, hit or miss, so no terminal state can strand a loading spinner.
 */
export function searchSelectionTransition(
  state: SearchSelectionState,
  event: SearchSelectionEvent
): SearchSelectionStep {
  if (event.kind === "frame-restored") {
    const outcome = resolveFrameRestoreSelection({
      pending: state.pending,
      restoredFrameRecordId: event.frameRecordId,
      restoredNodeIds: event.nodeIds
    });

    return {
      // Every restore re-adjudicates the failure notice unconditionally: the
      // notice names a node in the frame we just left, so it cannot survive.
      state: { pending: null, unavailable: outcome.kind === "unavailable" ? outcome.nodeId : null },
      selectNodeId: outcome.kind === "selected" ? outcome.nodeId : null
    };
  }

  if (event.kind === "cross-frame-jump") {
    return {
      state: { pending: { frameRecordId: event.frameRecordId, nodeId: event.nodeId }, unavailable: null },
      selectNodeId: null
    };
  }

  // An explicit node pick replaces the jump outright; a view or edge change is a
  // fresh action that only retires the stale failure notice, leaving an in-flight
  // jump to land on the frame it targets.
  return {
    state: { pending: event.kind === "select-node" ? null : state.pending, unavailable: null },
    selectNodeId: null
  };
}
